import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import apiService from "../services/apiService";
import "../styles/LoginPage.css";
import BloomWatchLogo from "../Icons/06 Bloomwatch Logo 1.1 1.png";

const LoginPage = () => {
  const { login } = useAuth();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Client Selection
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const navigate = useNavigate();

  // Step 1: Send OTP to email
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email before setting loading state
    if (!email || !email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      // Send OTP without client selection
      const response = await apiService.sendOTP(email.trim());

      setUserInfo({
        name: response.user || "User",
        email: email.trim(),
      });

      // Move to OTP verification step
      setStep(2);
      setError("");
    } catch (err) {
      console.error("Send OTP error:", err);
      // Handle specific error messages from backend
      if (err.message && err.message.includes("Invalid email address")) {
        setError("Invalid email address. Please check and try again.");
      } else if (err.message && err.message.includes("User not found")) {
        setError("Email address not found in our system.");
      } else {
        setError(
          err.message || "Failed to send verification code. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP and get available clients
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");

    // Validate OTP before setting loading state
    if (!otp || !otp.trim()) {
      setError("Please enter the OTP code.");
      return;
    }

    // Basic OTP validation (assuming it should be numeric and of certain length)
    if (otp.trim().length < 6) {
      setError("Please enter a valid 6-digit OTP code.");
      return;
    }

    setLoading(true);

    try {
      // Verify OTP and get available clients
      const data = await apiService.verifyOTP(email.trim(), otp.trim());

      // Validate response structure
      if (!data.user) {
        throw new Error("Invalid response from server");
      }

      if (!data.available_clients || !Array.isArray(data.available_clients)) {
        throw new Error("No client access found for this user");
      }

      // Update user info with response data
      setUserInfo({
        name: data.user.name || data.user,
        email: data.user.email || email.trim(),
      });

      setAvailableClients(data.available_clients);

      if (data.requires_client_selection && data.available_clients.length > 1) {
        // Multi-client user - show client selection
       
        setStep(3);
      } else if (data.available_clients.length === 1) {
        // Single-client user - auto-select and complete login
        
        setSelectedClient(data.available_clients[0].cl_id);
        await completeLogin(data.available_clients[0].cl_id);
      } else {
        throw new Error("No available clients found");
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      // Handle specific error messages
      if (err.message && err.message.includes("Invalid verification code")) {
        setError("Invalid verification code. Please check and try again.");
      } else if (err.message && err.message.includes("expired")) {
        setError("Verification code has expired. Please request a new one.");
      } else {
        setError(err.message || "Invalid OTP code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Client selection and final login
  const handleClientSelection = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedClient) {
      setError("Please select a client.");
      return;
    }

    setLoading(true);
    try {
      await completeLogin(selectedClient);
    } catch (err) {
      setError(err.message || "Failed to complete login.");
    } finally {
      setLoading(false);
    }
  };

  // Complete login with selected client
  const completeLogin = async (clientId) => {
    try {
      // Use the login-with-client endpoint (OTP is already verified and marked in backend)
      const data = await apiService.loginWithClient(email.trim(), clientId);

      // Validate response structure
      if (!data.token) {
        console.error("No token in response:", data);
        throw new Error("No authentication token received from server");
      }

      if (!data.data) {
        console.error("No data in response:", data);
        throw new Error("No user data received from server");
      }

      

      // Use AuthContext login method
      login(data.token, data.data);

      

      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        navigate("/home");
      }, 100);
    } catch (err) {
      console.error("Final login error:", err);
      // Handle specific error messages
      if (err.message && err.message.includes("Authentication failed")) {
        throw new Error("Authentication failed. Please try logging in again.");
      } else if (err.message && err.message.includes("Client access denied")) {
        throw new Error("Access denied for selected client.");
      } else {
        throw new Error(err.message || "Login failed. Please try again.");
      }
    }
  };

  // Go back to previous step
  const handleBack = () => {
    if (step === 3) {
      // Back from client selection to OTP
      setStep(2);
      setSelectedClient("");
    } else if (step === 2) {
      // Back from OTP to email
      setStep(1);
      setOtp("");
      setUserInfo(null);
    }

    setError("");
  };

  // Request new OTP code
  const handleRequestNewCode = async () => {
    setError("");
    setLoading(true);

    try {
      const response = await apiService.sendOTP(email.trim());

      setOtp(""); // Clear current OTP
      setError("");
      // Show success message briefly
      setError("New verification code sent to your email.");
      setTimeout(() => setError(""), 3000);
    } catch (err) {
      console.error("Request new OTP error:", err);
      setError(err.message || "Failed to send new verification code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>
            <img
              src={BloomWatchLogo}
              alt="BloomWatch Logo"
              className="login-logo"
            />
          </h1>
          <p>Secure Access Portal</p>
        </div>

        {step === 1 ? (
          // Step 1: Email Input
          <form onSubmit={handleSendOTP} className="login-form">
            <div className="form-group email-form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className={`login-button ${loading ? "loading" : ""}`}
              disabled={loading || !email}
            >
              {loading ? "Sending..." : "Send Verification Code"}
            </button>

            <div className="login-info">
              <p>
                Enter your registered email address to receive a verification
                code.
              </p>
            </div>
          </form>
        ) : step === 2 ? (
          // Step 2: OTP Verification
          <form onSubmit={handleVerifyOTP} className="login-form">
            <div className="user-info">
              <p>
                <strong>Welcome, {userInfo?.name}!</strong>
              </p>
              <p>Code sent to: {email}</p>
            </div>

            <div className="form-group otp-form-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
                disabled={loading}
                className="otp-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className={`login-button ${loading ? "loading" : ""}`}
              disabled={loading || otp.length !== 6}
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={handleBack}
              disabled={loading}
            >
              ← Back to Email
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={handleRequestNewCode}
              disabled={loading}
            >
              Request New Code
            </button>

            <div className="login-info">
              <p>Check your email for the 6-digit verification code.</p>
              <p>Code expires in 10 minutes.</p>
            </div>
          </form>
        ) : (
          // Step 3: Client Selection (for multi-client users)
          <form onSubmit={handleClientSelection} className="login-form">
            <div className="user-info">
              <p>
                <strong>Welcome, {userInfo?.name}!</strong>
              </p>
              <p>Email: {email}</p>
              <p>Please select a client to continue:</p>
            </div>

            <div className="form-group client-form-group">
              <label htmlFor="client">Select Client</label>
              <select
                id="client"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                required
                disabled={loading}
                className="client-select"
              >
                <option value="">-- Select a Client --</option>
                {availableClients.map((client) => (
                  <option key={client.cl_id} value={client.cl_id}>
                    {client.client_name} ({client.permission_level})
                  </option>
                ))}
              </select>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className={`login-button ${loading ? "loading" : ""}`}
              disabled={loading || !selectedClient}
            >
              {loading ? "Logging in..." : "Login"}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={handleBack}
              disabled={loading}
            >
              ← Back
            </button>

            <div className="login-info">
              <p>You have access to multiple clients. Select one to proceed.</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
